import * as THREE from 'three';
import { TunnelProfileDto } from 'providers/api';
import { ImportedSolidBoundingBox } from '../ImportedSolids/ImportedSolid';
import { buildMediaUrl, parseFullGeometryNearOrigin } from '../Meshes/geometryLoader';
import { isSceneSource, parseTunnelScene, tunnelSceneBoundingBox } from './tunnelScene';

export const DEFAULT_TUNNEL_PROFILE_OPACITY = 0.35;

export interface TunnelProfile {
  guid: string;
  fileName: string;
  sourceName?: string;
  fileData: string;
  boundingBox?: ImportedSolidBoundingBox;
  opacity: number;
  visible: boolean;
}

const toBoundingBox = (box: THREE.Box3): ImportedSolidBoundingBox => {
  const center = box.getCenter(new THREE.Vector3());
  return {
    minX: box.min.x,
    maxX: box.max.x,
    minY: box.min.y,
    maxY: box.max.y,
    minZ: box.min.z,
    maxZ: box.max.z,
    centerX: center.x,
    centerY: center.y,
    centerZ: center.z,
  };
};

export const buildTunnelProfile = (
  fileName: string,
  fileData: string,
  existingProfile?: TunnelProfile | null,
  sourceName = fileName,
): TunnelProfile | null => {
  // Compute the combined bounding box of the whole file for camera framing.
  let box: THREE.Box3 | null = null;
  if (isSceneSource(sourceName)) {
    // Rich per-entity scene JSON (DUF): bounding box straight from the positions.
    const scene = parseTunnelScene(fileData);
    if (!scene || scene.entities.length === 0) return null;
    box = tunnelSceneBoundingBox(scene);
  } else {
    // Plain OBJ/DXF: parse all geometry and measure it.
    const local = parseFullGeometryNearOrigin(fileData, sourceName);
    if (!local) return null;
    const { object, origin } = local;
    box = new THREE.Box3().setFromObject(object).translate(new THREE.Vector3(origin.x, origin.y, origin.z));
    object.traverse((child) => {
      const mesh = child as THREE.Mesh;
      if (mesh.geometry) mesh.geometry.dispose();
    });
  }

  const boundingBox = box.isEmpty() ? undefined : toBoundingBox(box);

  return {
    guid: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
    fileName,
    sourceName,
    fileData,
    boundingBox,
    opacity: existingProfile?.opacity ?? DEFAULT_TUNNEL_PROFILE_OPACITY,
    visible: true,
  };
};

/**
 * Build an in-memory tunnel profile from the persisted DTO returned by the API. Fetches the stored
 * geometry file from the media store and computes its bounding box. The stored file path is kept as
 * `sourceName` so callers can detect whether the persisted profile has actually changed (and avoid
 * re-fetching / re-framing on every model reload).
 */
export const buildTunnelProfileFromDto = async (dto: TunnelProfileDto | undefined | null): Promise<TunnelProfile | null> => {
  const path = dto?.geometryFile?.path;
  if (!path) return null;

  const response = await fetch(buildMediaUrl(path));
  if (!response.ok) return null;
  const fileData = await response.text();

  const profile = buildTunnelProfile(dto.geometryFile?.display ?? path, fileData, null, path);
  if (!profile) return null;

  return {
    ...profile,
    opacity: dto.opacity ?? DEFAULT_TUNNEL_PROFILE_OPACITY,
    visible: dto.visible ?? true,
  };
};
